/**
 * The Box score tab's wire types, copy and the two pure helpers its table
 * and tab share. The server formats every value and computes every total
 * (game-detail rule 7); this file only reads what it sent. ISSUE-990.
 *
 * Wire types are declared here, as in standingsTypes.ts, because the SDK's
 * private barrel re-exports hooks, not body types.
 */
import type { typePlayerSearch } from "../../routeState/playerSearch";

export type typeBoxScoreColumn = { key: string; label: string };
export type typeBoxScoreCell = { key: string; display: string };
export type typeBoxScoreRow = {
  playerId: string;
  name: string;
  position?: string | null;
  headshotUrl?: string | null;
  cells: typeBoxScoreCell[];
};
export type typeBoxScoreTable = {
  key: string;
  label: string;
  columns: typeBoxScoreColumn[];
  rows: typeBoxScoreRow[];
  totals: typeBoxScoreCell[];
};
export type typeBoxScoreTeam = { teamId: string; tables: typeBoxScoreTable[] };
export type typeBoxScoreSide = "away" | "home";
export type typeBoxScoreTeamInfo = {
  teamId: string;
  name: string;
  abbreviation: string;
};

export const BOX_SCORE_TEAM_SWITCHER_LABEL = "Box score team";
export const BOX_SCORE_PLAYER_HEADER = "Player";
export const BOX_SCORE_TOTALS_LABEL = "Totals";
export const BOX_SCORE_MISSING_CELL = "-";
export const BOX_SCORE_UNAVAILABLE = "The box score isn't ready yet.";
export const BOX_SCORE_TEAM_EMPTY = "No player stats for this team yet.";

/** One column's display text for a row or the totals; a hyphen when absent. */
export function boxScoreCellText(
  cells: readonly typeBoxScoreCell[],
  key: string,
): string {
  return (
    cells.find((cell) => cell.key === key)?.display ?? BOX_SCORE_MISSING_CELL
  );
}

/** Search params for /scores/player. `position` is left out when unknown. */
export function buildPlayerProfileSearch(input: {
  row: typeBoxScoreRow;
  team: typeBoxScoreTeamInfo;
}): typePlayerSearch {
  const { row, team } = input;
  const position =
    typeof row.position === "string" && row.position.trim() !== ""
      ? row.position
      : undefined;
  return {
    playerId: row.playerId,
    name: row.name,
    teamId: team.teamId,
    teamName: team.name,
    teamAbbreviation: team.abbreviation,
    ...(position === undefined ? {} : { position }),
  };
}
